import React from 'react';
import { Input } from '../ui/Input';
import type { ARMDetails } from '../../types/mortgageTypes';
import { calculateMonthlyRate, calculateMonthlyPayment } from '../../utils/mortgageCalculations';

interface ARMDetailsSectionProps {
  armDetails: ARMDetails;
  onChange: (details: ARMDetails) => void;
  principal: number;
  amortizationMonths: number;
  initialRate: number;
  country: 'USA' | 'Canada';
}

const formatCurrency = (value: number): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);

/** Remaining balance after `paymentsMade` monthly payments on a loan. */
const remainingBalance = (
  principal: number,
  monthlyRate: number,
  totalPayments: number,
  paymentsMade: number
): number => {
  if (monthlyRate === 0) {
    const payment = principal / totalPayments;
    return Math.max(0, principal - payment * paymentsMade);
  }
  const payment = calculateMonthlyPayment(principal, monthlyRate, totalPayments);
  const balance =
    principal * Math.pow(1 + monthlyRate, paymentsMade) -
    payment * ((Math.pow(1 + monthlyRate, paymentsMade) - 1) / monthlyRate);
  return Math.max(0, balance);
};

export const ARMDetailsSection: React.FC<ARMDetailsSectionProps> = ({
  armDetails,
  onChange,
  principal,
  amortizationMonths,
  initialRate,
  country,
}) => {
  const handleChange = (field: keyof ARMDetails, value: number) => {
    onChange({
      ...armDetails,
      [field]: value,
    });
  };

  const fullyIndexedRate = armDetails.indexRate + armDetails.margin;

  // Compute balance remaining at the end of the fixed period
  const fixedMonths = armDetails.initialFixedPeriod * 12;
  const remainingMonths = Math.max(1, amortizationMonths - fixedMonths);
  const initialMonthlyRate = calculateMonthlyRate(initialRate, country, 'adjustable');
  const balanceAtReset =
    principal > 0 && amortizationMonths > fixedMonths
      ? remainingBalance(principal, initialMonthlyRate, amortizationMonths, fixedMonths)
      : 0;

  const currentPayment =
    principal > 0
      ? calculateMonthlyPayment(principal, initialMonthlyRate, amortizationMonths)
      : 0;

  // Three rate scenarios at first adjustment
  const worstRate = initialRate + armDetails.lifetimeCap;
  const lifetimeMin = Math.max(0, initialRate - armDetails.lifetimeCap);

  // Expected: fully indexed rate, capped by initial adjustment cap and lifetime cap
  const expectedRateRaw = Math.min(
    Math.max(fullyIndexedRate, initialRate - armDetails.initialAdjustmentCap),
    initialRate + armDetails.initialAdjustmentCap
  );
  const expectedRate = Math.min(Math.max(expectedRateRaw, lifetimeMin), worstRate);

  const paymentForRate = (annualRate: number): number => {
    if (balanceAtReset <= 0 || remainingMonths <= 0) return 0;
    const mr = calculateMonthlyRate(annualRate, country, 'adjustable');
    return calculateMonthlyPayment(balanceAtReset, mr, remainingMonths);
  };

  const bestPayment = paymentForRate(lifetimeMin);
  const expectedPayment = paymentForRate(expectedRate);
  const worstPayment = paymentForRate(worstRate);

  const showScenarios = balanceAtReset > 0 && principal > 0 && amortizationMonths > fixedMonths;

  return (
    <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-md">
      <h4 className="font-semibold text-amber-900 mb-3">ARM (Adjustable Rate Mortgage) Details</h4>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="max-w-[280px]">
          <Input
            label="Initial Fixed Period"
            type="number"
            value={armDetails.initialFixedPeriod}
            onChange={(e) => handleChange('initialFixedPeriod', Number(e.target.value))}
            suffix="years"
          />
          <p className="text-xs text-amber-700 mt-1">e.g., 5 for 5/1 ARM, 7 for 7/1 ARM</p>
        </div>

        <div className="max-w-[280px]">
          <Input
            label="Adjustment Frequency"
            type="number"
            value={armDetails.adjustmentFrequency}
            onChange={(e) => handleChange('adjustmentFrequency', Number(e.target.value))}
            suffix="months"
          />
          <p className="text-xs text-amber-700 mt-1">12 = annual, 6 = semi-annual</p>
        </div>

        <div className="max-w-[280px]">
          <Input
            label="Index Rate"
            type="number"
            value={armDetails.indexRate}
            onChange={(e) => handleChange('indexRate', Number(e.target.value))}
            suffix="%"
          />
          <p className="text-xs text-amber-700 mt-1">Current market index rate</p>
        </div>

        <div className="max-w-[280px]">
          <Input
            label="Margin"
            type="number"
            value={armDetails.margin}
            onChange={(e) => handleChange('margin', Number(e.target.value))}
            suffix="%"
          />
          <p className="text-xs text-amber-700 mt-1">Lender's margin added to index</p>
        </div>

        <div className="max-w-[280px]">
          <Input
            label="Initial Adjustment Cap"
            type="number"
            value={armDetails.initialAdjustmentCap}
            onChange={(e) => handleChange('initialAdjustmentCap', Number(e.target.value))}
            suffix="%"
          />
          <p className="text-xs text-amber-700 mt-1">Max rate change at first adjustment</p>
        </div>

        <div className="max-w-[280px]">
          <Input
            label="Periodic Cap"
            type="number"
            value={armDetails.periodicCap}
            onChange={(e) => handleChange('periodicCap', Number(e.target.value))}
            suffix="%"
          />
          <p className="text-xs text-amber-700 mt-1">Max rate change per period</p>
        </div>

        <div className="max-w-[280px]">
          <Input
            label="Lifetime Cap"
            type="number"
            value={armDetails.lifetimeCap}
            onChange={(e) => handleChange('lifetimeCap', Number(e.target.value))}
            suffix="%"
          />
          <p className="text-xs text-amber-700 mt-1">Max rate over life of loan</p>
        </div>
      </div>

      {/* Fully Indexed Rate */}
      <div className="mt-4 p-3 bg-amber-100 rounded-md">
        <p className="text-sm text-amber-900">
          <strong>Fully Indexed Rate:</strong> {fullyIndexedRate.toFixed(3)}%
          (Index {armDetails.indexRate}% + Margin {armDetails.margin}%)
        </p>
        <p className="text-xs text-amber-700 mt-1">
          Your rate will adjust to the fully indexed rate after the initial fixed period, subject to caps.
        </p>
      </div>

      {/* Payment Scenario Callout */}
      {showScenarios && (
        <div className="mt-4 p-4 bg-white border border-amber-300 rounded-md">
          <h5 className="text-sm font-semibold text-amber-900 mb-1">
            After your {armDetails.initialFixedPeriod}-year fixed period - what could your payment become?
          </h5>
          <p className="text-xs text-amber-700 mb-3">
            Based on a remaining balance of {formatCurrency(balanceAtReset)} and{' '}
            {remainingMonths} months left. Your current payment is{' '}
            <strong>{formatCurrency(currentPayment)}/mo</strong>.
          </p>

          <div className="grid grid-cols-3 gap-3">
            {/* Best case */}
            <div className="bg-green-50 border border-green-200 rounded-md p-3 text-center">
              <p className="text-xs text-green-700 font-medium mb-1">Best Case</p>
              <p className="text-lg font-bold text-green-800">{formatCurrency(bestPayment)}<span className="text-xs font-normal">/mo</span></p>
              <p className="text-xs text-green-600 mt-1">Rate: {lifetimeMin.toFixed(2)}%</p>
              <p className="text-xs text-green-500">(lifetime floor)</p>
            </div>

            {/* Expected */}
            <div className="bg-amber-50 border border-amber-300 rounded-md p-3 text-center">
              <p className="text-xs text-amber-700 font-medium mb-1">Expected</p>
              <p className="text-lg font-bold text-amber-900">{formatCurrency(expectedPayment)}<span className="text-xs font-normal">/mo</span></p>
              <p className="text-xs text-amber-600 mt-1">Rate: {expectedRate.toFixed(2)}%</p>
              <p className="text-xs text-amber-500">(today's index + margin)</p>
            </div>

            {/* Worst case */}
            <div className="bg-red-50 border border-red-200 rounded-md p-3 text-center">
              <p className="text-xs text-red-700 font-medium mb-1">Worst Case</p>
              <p className="text-lg font-bold text-red-800">{formatCurrency(worstPayment)}<span className="text-xs font-normal">/mo</span></p>
              <p className="text-xs text-red-600 mt-1">Rate: {worstRate.toFixed(2)}%</p>
              <p className="text-xs text-red-500">(lifetime cap)</p>
            </div>
          </div>

          <p className="text-xs text-gray-500 mt-3">
            Scenarios assume the index rate rises, stays flat, or falls to the cap limits. Actual rate
            depends on market conditions at each adjustment date.
          </p>
        </div>
      )}
    </div>
  );
};
